import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  Paper
} from '@mui/material';
import { motion } from 'framer-motion';
import axios from 'axios';

const GDPRConsentModal = ({ open, onAccept, onDecline, userData }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checked, setChecked] = useState(false);

  const handleAccept = async () => {
    if (!checked) {
      setError('You must check the box to confirm you have read and agreed to the terms');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Call the parent component's onAccept handler directly
      // The parent will handle the GDPR consent submission
      onAccept();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit GDPR consent');
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      maxWidth="md" 
      fullWidth 
      PaperComponent={motion.div}
      PaperProps={{
        initial: { opacity: 0, y: -20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.3 },
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, backgroundColor: 'background.paper', px: 3, pt: 3 }}>
        <Typography variant="h4" component="div" fontWeight={600}>
          Data Protection Consent
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ backgroundColor: 'background.paper', px: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
            borderRadius: '8px',
            mb: 3
          }}
        >
          <Typography variant="h6" gutterBottom>
            Privacy Policy & GDPR Consent
          </Typography>
          
          <Typography variant="body1" paragraph>
            Welcome to the Works Council Election Portal. Before proceeding, we need your consent to process your personal data in accordance with the General Data Protection Regulation (GDPR).
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>Data We Collect:</strong> We collect and process your name, email address, business unit, and joining date to facilitate your participation in works council elections.
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>How We Use Your Data:</strong> Your data will be used to authenticate you, enable your participation in nominations and voting, and ensure the proper functioning of the election process.
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>Data Retention:</strong> Your data will be retained for the duration of your employment plus any additional period required by applicable law.
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>Your Rights:</strong> Under GDPR, you have the right to access, rectify, and erase your personal data, as well as the right to restrict or object to processing.
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>Data Security:</strong> We implement appropriate technical and organizational measures to protect your personal data.
          </Typography>
        </Paper>
        
        <FormControlLabel
          control={
            <Checkbox 
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              color="primary"
            />
          }
          label="I have read and agree to the privacy policy and consent to the processing of my personal data"
        />
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0, backgroundColor: 'background.paper' }}>
        <Button 
          onClick={onDecline}
          color="error"
          variant="outlined"
          sx={{ borderRadius: '8px' }}
        >
          Decline
        </Button>
        
        <Button 
          onClick={handleAccept}
          color="primary"
          variant="contained"
          disabled={loading || !checked}
          sx={{
            borderRadius: '8px',
            position: 'relative',
            minWidth: '100px'
          }}
        >
          {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : "Accept"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GDPRConsentModal;
